<template>
  <section
    v-if="hasApproveRules || hasCodeOwnersRules"
    class="merge-request-approve-rules"
  >
    <div
      class="merge-request-approve-rules__toggle"
      @click="onShowRules"
    >
      <div class="title">
        <i class="gf-icon gf-icon-user-check"></i>
        <b>{{ $t('approval_rules.title') }}</b>
      </div>
      <i
        class="gf-icon"
        :class="toggleIconClass"
      ></i>
    </div>
    <div
      v-if="areRulesVisible"
      class="merge-request-approve-rules__content"
    >
      <div
        v-if="hasApproveRules"
        class="merge-request-approve-rules__branches"
      >
        <h6 class="subtitle">{{ $t('page.approvers') }}</h6>
        <div class="merge-request-approve-rules__rules">
          <div class="merge-request-approve-rules__row --header">
            <div class="merge-request-approve-rules__cell --title">
              {{ $t('approval_rules.table.h1') }}
            </div>
            <div class="merge-request-approve-rules__cell --approvers">
              {{ $t('approval_rules.table.h2') }}
            </div>
            <div class="merge-request-approve-rules__cell --condition">
              {{ $t('approval_rules.table.h3') }}
            </div>
            <div class="merge-request-approve-rules__cell --approved-by">
              {{ $t('approval_rules.table.h4') }}
            </div>
          </div>
          <div
            v-for="approvedRule in mergeRequestApprovedBy.approvedByRules"
            :key="approvedRule.id"
            class="merge-request-approve-rules__row"
          >
            <div
              class="merge-request-approve-rules__cell --title"
              :data-title="$t('approval_rules.table.h1')"
            >
              {{ approvedRule?.rule?.name }}
            </div>
            <div
              class="merge-request-approve-rules__cell --approvers"
              :data-title="$t('approval_rules.table.h2')"
            >
              <div
                class="users"
                :style="{ 'flex-wrap': areAllAllowedApproversVisible ? 'wrap' : '' }"
              >
                <user-label-item
                  v-for="user in approvedRule.rule.allowedApprovers.slice(0, visibleAllowedAssignedUsers)"
                  :key="user.id"
                  :user="user"
                  :has-user-link="true"
                  :show-name="false"
                  :show-tooltip="true"
                  :tooltip-prefix="'rule-1-prefix-' + approvedRule.id + '--'"
                />

                <gf-button
                  v-if="
                    !areAllAllowedApproversVisible && approvedRule.rule.allowedApprovers?.length > maxVisibleUsersNumber
                  "
                  variant="link"
                  style="padding-left: 0"
                  :text="`+${approvedRule.rule.allowedApprovers?.length - maxVisibleUsersNumber}`"
                  @click="showAllAllowedApprovers"
                ></gf-button>
              </div>
            </div>
            <div
              class="merge-request-approve-rules__cell --condition"
              :data-title="$t('approval_rules.table.h3')"
            >
              {{ approvedRule?.approvalsCount }}/{{ approvedRule?.rule?.minimalApprovals }}
            </div>
            <div
              class="merge-request-approve-rules__cell --approved-by"
              :data-title="$t('approval_rules.table.h4')"
            >
              <div
                class="users"
                :style="{ 'flex-wrap': areAllApprovedAllowedApproversVisible ? 'wrap' : '' }"
              >
                <user-label-item
                  v-for="user in approvedRule.approvers.slice(0, visibleAllowedAssignedUsersApproved)"
                  :key="user.id"
                  :user="user"
                  :has-user-link="true"
                  :show-name="false"
                  :show-tooltip="true"
                  :tooltip-prefix="'rule-1-prefix-' + approvedRule.id + '--'"
                />

                <gf-button
                  v-if="!areAllApprovedAllowedApproversVisible && getMoreApprovedNumber(approvedRule)"
                  variant="link"
                  style="padding-left: 0"
                  :text="`+${getMoreApprovedNumber(approvedRule)}`"
                  @click="showAllApprovedAllowedApprovers"
                ></gf-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="hasCodeOwnersRules"
        class="merge-request-approve-rules__codeowners"
      >
        <h6 class="subtitle">{{ $t('code_owners.title') }}</h6>
        <div class="merge-request-approve-rules__rules">
          <div class="merge-request-approve-rules__row --header">
            <div class="merge-request-approve-rules__cell --title">
              {{ $t('code_owners.table.h1') }}
            </div>
            <div class="merge-request-approve-rules__cell --approvers">
              {{ $t('code_owners.table.h2') }}
            </div>
            <div class="merge-request-approve-rules__cell --condition">
              {{ $t('code_owners.table.h3') }}
            </div>
            <div class="merge-request-approve-rules__cell --approved-by">
              {{ $t('code_owners.table.h4') }}
            </div>
          </div>
          <div
            v-for="(codeOwnerRule, index) in codeOwners"
            :key="index"
            class="merge-request-approve-rules__row"
          >
            <div
              class="merge-request-approve-rules__cell --title"
              :data-title="$t('approval_rules.table.h1')"
            >
              <div class="title">
                <small class="subtext">{{ codeOwnerRule?.section }}</small>
                <span>{{ codeOwnerRule?.pattern }}</span>
              </div>
            </div>
            <div
              class="merge-request-approve-rules__cell --approvers"
              :data-title="$t('approval_rules.table.h2')"
            >
              <div
                class="users"
                :style="{ 'flex-wrap': areAllCodeOwnersVisible ? 'wrap' : '' }"
              >
                <user-label-item
                  v-for="user in codeOwnerRule?.owners?.slice(0, visibleCodeOwners)"
                  :key="user.id"
                  :user="user"
                  :has-user-link="true"
                  :show-name="false"
                  :show-tooltip="true"
                  :tooltip-prefix="'rule-1-prefix-' + index + '--'"
                />

                <gf-button
                  v-if="!areAllCodeOwnersVisible && codeOwnerRule.owners?.length > maxVisibleUsersNumber"
                  variant="link"
                  style="padding-left: 0"
                  :text="`+${codeOwnerRule.owners?.length - maxVisibleUsersNumber}`"
                  @click="showAllCodeOwners"
                ></gf-button>
              </div>
            </div>
            <div
              class="merge-request-approve-rules__cell --condition"
              :data-title="$t('approval_rules.table.h3')"
            >
              {{ codeOwnerRule?.allApprovedCount || 0 }}/{{ codeOwnerRule?.approver?.length || 1 }}
            </div>
            <div
              class="merge-request-approve-rules__cell --approved-by"
              :data-title="$t('approval_rules.table.h4')"
            >
              <div
                class="users"
                :style="{ 'flex-wrap': areAllApprovedAllowedApproversVisible ? 'wrap' : '' }"
              >
                <user-label-item
                  v-for="user in codeOwnerRule?.approver?.slice(0, visibleApprovedCodeOwners)"
                  :key="user.id"
                  :user="user"
                  :has-user-link="true"
                  :show-name="false"
                  :show-tooltip="true"
                  :tooltip-prefix="'rule-1-prefix-' + user.id + '--'"
                />

                <gf-button
                  v-if="!areAllApprovedCodeOwnersVisible && codeOwnerRule?.approver?.length > maxVisibleUsersNumber"
                  variant="link"
                  style="padding-left: 0"
                  :text="`+${getMoreCodeOwnersNumber(codeOwnerRule)}`"
                  @click="showAllApprovedCodeOwners"
                ></gf-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// eslint-disable-next-line max-len
import MergeRequestApprovalMessages from '@shared/modules/merge-request-approver/merge-request-approval.messages';
// eslint-disable-next-line max-len
import MergeRequestApproverService from '@shared/modules/merge-request-approver/merge-request-approver.service';
import MergeRequestService from '@shared/modules/merge-request/merge-request.service';
import UserLabelItem from '@shared/shared-components/shared/user-label-item';
import GfButton from '@/shared-modules/shared-components/shared/components/GfButton';

export default {
  name: 'MergeRequestApproveRules',
  components: {
    UserLabelItem,
    GfButton
  },
  data() {
    return {
      areRulesVisible: false,
      areAllAllowedApproversVisible: false,
      areAllApprovedAllowedApproversVisible: false,
      areAllCodeOwnersVisible: false,
      areAllApprovedCodeOwnersVisible: false
    };
  },
  computed: {
    alias() {
      return this.$route.params?.alias;
    },
    projectAlias() {
      return this.$route.params?.projectAlias;
    },
    mergeRequestId() {
      return this.$route.params?.id;
    },
    toggleIconClass() {
      return this.areRulesVisible ? 'gf-icon-chevron-up' : 'gf-icon-chevron-down';
    },
    mergeRequestApprovedBy() {
      return this.$store.getters.getMergeRequestApprovedBy;
    },
    codeOwners() {
      return this.$store.getters.getCodeOwners;
    },
    applicationVersion() {
      return this.$store.getters.getApplicationVersion;
    },
    hasApproveRules() {
      return this.mergeRequestApprovedBy.approvedByRules && this.mergeRequestApprovedBy.approvedByRules.length > 0;
    },
    hasCodeOwnersRules() {
      return this.applicationVersion !== 'onpremise' && this.codeOwners?.length > 0;
    },
    maxVisibleUsersNumber() {
      if (window.innerWidth < 576) {
        return 5;
      }
      if (window.innerWidth < 768) {
        return 12;
      }
      if (window.innerWidth < 992) {
        return 2;
      }
      if (window.innerWidth < 1200) {
        return 2;
      }
      if (window.innerWidth < 1400) {
        return 3;
      }
      return 5;
    },
    visibleAllowedAssignedUsers() {
      return this.areAllAllowedApproversVisible
        ? this.approvedRule?.rule?.allowedApprovers?.length
        : this.maxVisibleUsersNumber;
    },
    visibleAllowedAssignedUsersApproved() {
      return this.areAllApprovedAllowedApproversVisible
        ? this.approvedRule?.approvers.length
        : this.maxVisibleUsersNumber;
    },
    visibleCodeOwners() {
      return this.areAllCodeOwnersVisible ? this.codeOwnerRule?.owners?.length : this.maxVisibleUsersNumber;
    },
    visibleApprovedCodeOwners() {
      return this.areAllApprovedCodeOwnersVisible ? this.codeOwnerRule?.approver?.length : this.maxVisibleUsersNumber;
    }
  },
  mounted() {
    this.getMergeRequestApprovedBy();
    if (this.applicationVersion !== 'onpremise') {
      this.getMergeRequestCodeOwners();
    }
  },
  methods: {
    onShowRules() {
      this.areRulesVisible = !this.areRulesVisible;
    },
    getMergeRequestApprovedBy() {
      MergeRequestApproverService.getMergeRequestApprovedBy(this.alias, this.projectAlias, this.mergeRequestId);
    },
    getMergeRequestCodeOwners() {
      MergeRequestService.getMergeRequestCodeOwners(this.alias, this.projectAlias, this.mergeRequestId);
    },
    getEmptyApprovedNumber(approvedRule) {
      return this.countEmptyCirclesNumber(
        approvedRule?.approvers?.length,
        this.maxVisibleUsersNumber,
        approvedRule?.rule?.minimalApprovals
      );
    },
    getMoreApprovedNumber(approvedRule) {
      return this.countMoreCirclesAvailableNumber(
        approvedRule?.approvers?.length,
        this.maxVisibleUsersNumber,
        approvedRule?.rule?.minimalApprovals
      );
    },
    getEmptyCodeOwnersNumber(codeOwnerRule) {
      return this.countEmptyCirclesNumber(
        codeOwnerRule?.approved || 0,
        this.maxVisibleUsersNumber,
        codeOwnerRule?.approver?.length || 1
      );
    },
    getMoreCodeOwnersNumber(codeOwnerRule) {
      return this.countMoreCirclesAvailableNumber(
        codeOwnerRule?.approved || 0,
        this.maxVisibleUsersNumber,
        codeOwnerRule?.approver?.length || 1
      );
    },
    countEmptyCirclesNumber(approvedNumber, maxVisible, minApprovals) {
      if (approvedNumber <= maxVisible && minApprovals > approvedNumber) {
        return Math.min(minApprovals - approvedNumber, maxVisible - approvedNumber);
      }
      return 0;
    },
    countMoreCirclesAvailableNumber(approvedNumber, maxVisible, minApprovals) {
      if (minApprovals > approvedNumber && minApprovals - approvedNumber > maxVisible) {
        return minApprovals - maxVisible;
      }
      if (approvedNumber > maxVisible) {
        return approvedNumber - maxVisible;
      }
      return 0;
    },
    showAllAllowedApprovers() {
      this.areAllAllowedApproversVisible = !this.areAllAllowedApproversVisible;
    },
    showAllApprovedAllowedApprovers() {
      this.areAllApprovedAllowedApproversVisible = !this.areAllApprovedAllowedApproversVisible;
    },
    showAllCodeOwners() {
      this.areAllCodeOwnersVisible = !this.areAllCodeOwnersVisible;
    },
    showAllApprovedCodeOwners() {
      this.areAllApprovedCodeOwnersVisible = !this.areAllApprovedCodeOwnersVisible;
    }
  },
  i18n: {
    messages: MergeRequestApprovalMessages
  }
};
</script>

<style lang="scss">
$this: '.merge-request-approve-rules';
#{$this} {
  display: flex;
  flex-direction: column;
  gap: 12px;

  #{$this}__toggle {
    padding: 12px 8px;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    cursor: pointer;

    & > .title {
      font-size: 16px;

      & > i {
        margin-right: 4px;
      }
    }
  }

  #{$this}__content {
    display: flex;
    flex-direction: column;
    gap: 12px;

    #{$this}__rules {
      display: grid;
      grid-template-columns: minmax(auto, 270px) 1fr min-content 1fr;
      border: 1px solid var(--border-color);
      border-radius: 6px;

      @include theme.max-xxl {
        grid-template-columns: minmax(auto, 220px) 1fr min-content 1fr;
      }

      @include theme.max-xl {
        grid-template-columns: minmax(auto, 135px) 1fr min-content 1fr;
      }

      @include theme.max-lg {
        grid-template-columns: minmax(auto, 142px) 1fr min-content 1fr;
      }

      @include theme.max-md {
        display: flex;
        flex-direction: column;
        gap: 8px;
        border: none;
      }

      #{$this}__row {
        display: contents;

        @include theme.max-md {
          padding: 12px 16px;
          display: grid;
          grid-template-areas:
            'title title'
            'approvers approvers'
            'approved-by condition';
          grid-template-columns: 1fr min-content;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          gap: 16px;
        }

        &:last-child {
          #{$this}__cell {
            border-bottom: none;
          }
        }

        &.--header {
          font-weight: 550;
          letter-spacing: 0.015rem;

          @include theme.max-md {
            display: none;
          }
        }

        #{$this}__cell {
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-color);

          @include theme.max-md {
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: 4px;
            border-bottom: none;

            &::before {
              content: attr(data-title);
              font-weight: 550;
              letter-spacing: 0.015rem;
            }
          }

          &.--title {
            overflow-wrap: break-word;

            @include theme.max-md {
              grid-area: title;
            }

            & > .title {
              display: flex;
              flex-direction: column;

              @include theme.max-md {
                flex-direction: row;
                align-items: center;
                gap: 8px;
              }

              & > .subtext {
                color: var(--text-muted);
              }
            }
          }
          &.--approvers {
            & > .users {
              display: flex;
              align-items: center;
              gap: 4px;
            }

            @include theme.max-md {
              grid-area: approvers;
            }
          }
          &.--condition {
            @include theme.max-md {
              grid-area: condition;
            }
          }
          &.--approved-by {
            @include theme.max-md {
              grid-area: approved-by;
            }

            & > .users {
              display: flex;
              align-items: center;
              gap: 4px;

              & > .empty-approver {
                width: 28px;
                height: 28px;
                display: block;
                border: 1px solid var(--border-color);
                border-radius: 14px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
